import { useEffect, useState } from "react";
import { AlertTriangle, Database, ChevronRight, HardDrive, Sparkles, UserRound, Sliders } from "lucide-react";
import { api } from "../../lib/api";
import type { View } from "../../app/views";
import type { AiUsage } from "./AiSettings";

interface BackupStatus {
  configured: boolean;
  latest?: string | null;
  stale?: boolean;
}

function settingsStats(ai: AiUsage | null, backup: BackupStatus | null, errorCount: number | null) {
  const used = ai ? Object.values(ai.usage).reduce((n, v) => n + (v || 0), 0) : null;
  const aiAlert = ai != null && used != null && used >= ai.limit;
  const backupValue = !backup
    ? "…"
    : !backup.configured
      ? "Unavailable"
      : !backup.latest
        ? "None yet"
        : backup.stale
          ? "Overdue"
          : "Up to date";
  const backupAlert = !!backup?.configured && (!backup.latest || !!backup.stale);
  return [
    {
      label: "AI",
      value: ai && used != null ? `${used}/${ai.limit}` : "…",
      alert: aiAlert,
      view: "ai" as const,
    },
    {
      label: "Backups",
      value: backupValue,
      alert: backupAlert,
      view: "backups" as const,
    },
    {
      label: "Errors",
      value: errorCount == null ? "…" : errorCount === 0 ? "Clear" : `${errorCount} recent`,
      alert: (errorCount ?? 0) > 0,
      view: "errors" as const,
    },
  ];
}

export default function SettingsHome({ nav, online = true, staging = false }: { nav: (view: View) => void; online?: boolean; staging?: boolean }) {
  const [ai, setAi] = useState<AiUsage | null>(null);
  const [backup, setBackup] = useState<BackupStatus | null>(null);
  const [errorCount, setErrorCount] = useState<number | null>(null);

  useEffect(() => {
    if (!online) {
      setAi(null);
      setBackup(null);
      setErrorCount(null);
      return;
    }
    let active = true;
    Promise.allSettled([
      api<AiUsage>("ai-usage"),
      api<BackupStatus>("backup-status"),
      api<{ errors: unknown[] }>("errors"),
    ]).then(([aiResult, backupResult, errorsResult]) => {
      if (!active) return;
      setAi(aiResult.status === "fulfilled" ? aiResult.value : null);
      setBackup(backupResult.status === "fulfilled" ? backupResult.value : { configured: false });
      setErrorCount(errorsResult.status === "fulfilled" ? (errorsResult.value.errors?.length ?? 0) : null);
    });
    return () => {
      active = false;
    };
  }, [online]);

  const stats = online
    ? settingsStats(ai, backup, errorCount)
    : [
        { label: "AI", value: "Offline", alert: false, view: "ai" as const },
        { label: "Backups", value: "Offline", alert: false, view: "backups" as const },
        { label: "Errors", value: "Offline", alert: false, view: "errors" as const },
      ];

  return (
    <section className="panel settings-panel settings-home">
      <div className="more-hub-stats" role="group" aria-label="App health">
        {stats.map((stat) => (
          <button
            key={stat.view}
            type="button"
            className={stat.alert ? "is-alert" : undefined}
            disabled={!online}
            title={online ? undefined : "Available when online"}
            onClick={() => nav(stat.view)}
          >
            <span>{stat.label}</span>
            <strong>{stat.value}</strong>
          </button>
        ))}
      </div>
      <button className="settings-link" disabled={!online} title={online ? undefined : "Available when online"} onClick={() => nav("profile-settings")}>
        <span className="settings-link-icon">
          <UserRound size={20} />
        </span>
        <span className="settings-link-text">
          <strong>Profiles</strong>
          <small>{online ? "Add people to the household and choose their pictures." : "Needs a connection."}</small>
        </span>
        <ChevronRight size={18} />
      </button>
      <button className="settings-link" disabled={!online} title={online ? undefined : "Available when online"} onClick={() => nav("ai")}>
        <span className="settings-link-icon">
          <Sparkles size={20} />
        </span>
        <span className="settings-link-text">
          <strong>AI</strong>
          <small>{online ? "Provider preference and today's shared allowance." : "Needs a connection."}</small>
        </span>
        <ChevronRight size={18} />
      </button>
      <button className="settings-link" disabled={!online} title={online ? undefined : "Available when online"} onClick={() => nav("smart-rules")}>
        <span className="settings-link-icon">
          <Sliders size={20} />
        </span>
        <span className="settings-link-text">
          <strong>Smart Rules & Ingestion</strong>
          <small>{online ? "Card mappings, merchant rules, and SMS automation webhook." : "Needs a connection."}</small>
        </span>
        <ChevronRight size={18} />
      </button>
      <button className="settings-link" disabled={!online} title={online ? undefined : "Available when online"} onClick={() => nav("backups")}>
        <span className="settings-link-icon">
          <HardDrive size={20} />
        </span>
        <span className="settings-link-text">
          <strong>Backups</strong>
          <small>{online ? "Whether the daily ledger copy finished." : "Needs a connection."}</small>
        </span>
        <ChevronRight size={18} />
      </button>
      <button className="settings-link" disabled={!online} title={online ? undefined : "Available when online"} onClick={() => nav("errors")}>
        <span className="settings-link-icon">
          <AlertTriangle size={20} />
        </span>
        <span className="settings-link-text">
          <strong>Error logs</strong>
          <small>{online ? "See the most recent errors, server and client." : "Needs a connection."}</small>
        </span>
        <ChevronRight size={18} />
      </button>
      {staging && <button className="settings-link" disabled={!online} onClick={() => nav("staging-data")}>
        <span className="settings-link-icon"><Database size={20} /></span>
        <span className="settings-link-text"><strong>Staging tools</strong><small>Reset the test ledger or add a small sample.</small></span>
        <ChevronRight size={18} />
      </button>}
    </section>
  );
}
